import type { PayloadAction } from '@reduxjs/toolkit';
import { createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { groupApi } from '../../api/groups.api';
import type { Group, VehiclesInGroup } from '../../types/Group';
import type { RootState } from '../store';

const emptyArray: any[] = [];

const groupsAdapter = createEntityAdapter<Group, string>({
  selectId: group => group.id,
});
const dropdownGroupsAdapter = createEntityAdapter<Group>();

const groupsInitialState = groupsAdapter.getInitialState({
  count: 0,
  selectedGroupId: null as string | null,
  vehicleMap: {} as Record<string, VehiclesInGroup[]>,
  vehicleCounts: {} as Record<string, number>,
  allVehiclesMap: {} as Record<string, VehiclesInGroup[]>,
  allVehicleCounts: {} as Record<string, number>,
});

const dropdownGroupsInitialState = dropdownGroupsAdapter.getInitialState();

const initialState = {
  groups: groupsInitialState,
  dropdownGroups: dropdownGroupsInitialState,
};

export const groupsReducer = createSlice({
  name: 'groupsReducer',
  initialState,
  reducers: {
    setSelectedGroup: (state, action: PayloadAction<string>) => {
      state.groups.selectedGroupId = action.payload;
    },
    removeGroup: (state, action: PayloadAction<string>) => {
      groupsAdapter.removeOne(state.groups, action.payload);
      state.groups.count = Math.max(0, state.groups.count - 1);
    },
  },
  extraReducers: builder => {
    builder
      // For paginated group list (used in tables)
      .addMatcher(groupApi.endpoints.getGroups.matchFulfilled, (state, action) => {
        groupsAdapter.setAll(state.groups, action.payload.results);
        state.groups.count = action.payload.count;
      })

      // For non-paginated group list (used in dropdowns)
      .addMatcher(groupApi.endpoints.getAllGroups.matchFulfilled, (state, action) => {
        dropdownGroupsAdapter.setAll(state.dropdownGroups, action.payload);
      })

      // On create group, add it to paginated list
      .addMatcher(groupApi.endpoints.createGroup.matchFulfilled, (state, action) => {
        groupsAdapter.addOne(state.groups, action.payload);
        state.groups.count += 1;
      })

      // On edit group, update it in paginated list
      .addMatcher(groupApi.endpoints.editGroup.matchFulfilled, (state, action) => {
        groupsAdapter.updateOne(state.groups, {
          id: action.payload.id,
          changes: action.payload,
        });
      })

      // Vehicles in group paginated
      .addMatcher(groupApi.endpoints.getVehiclesInGroup.matchFulfilled, (state, action) => {
        const groupId = action.meta.arg.originalArgs.group_id;
        if (groupId) {
          state.groups.vehicleMap[groupId] = action.payload.results;
          state.groups.vehicleCounts[groupId] = action.payload.count;
        }
      })

      //vehicles in group non-paginated
      .addMatcher(groupApi.endpoints.getAllVehiclesInGroup.matchFulfilled, (state, action) => {
        const groupId = action.meta.arg.originalArgs;
        if (groupId) {
          state.groups.allVehiclesMap[groupId] = action.payload;
          state.groups.allVehicleCounts[groupId] = action.payload.length;
        }
      });
  },
});

// Actions
export const { setSelectedGroup, removeGroup } = groupsReducer.actions;

// Selectors — paginated groups
export const groupsSelectors = groupsAdapter.getSelectors<RootState>(state => state.groupsReducer.groups);

// Selectors — dropdown groups
export const dropdownGroupsSelectors = dropdownGroupsAdapter.getSelectors<RootState>(
  state => state.groupsReducer.dropdownGroups,
);

// Count
export const groupCountSelector = (state: RootState) => state.groupsReducer.groups.count;

// Vehicles by group
export const selectVehiclesByGroupId = createSelector(
  [(state: RootState) => state.groupsReducer.groups.vehicleMap, (_: RootState, groupId: string) => groupId],
  (vehicleMap, groupId) => vehicleMap[groupId] ?? emptyArray,
);

export const selectVehicleCountByGroupId = (state: RootState, groupId: string) =>
  state.groupsReducer.groups.vehicleCounts[groupId] ?? 0;

// Non-paginated vehicles
export const selectAllVehiclesByGroupId = createSelector(
  [(state: RootState) => state.groupsReducer.groups.allVehiclesMap, (_: RootState, groupId: string) => groupId],
  (map, groupId) => map[groupId] ?? emptyArray,
);

export const selectAllVehicleCountByGroupId = (state: RootState, groupId: string) =>
  state.groupsReducer.groups.allVehicleCounts[groupId] ?? 0;
